import { Theme, Toolbar } from '@mui/material';
import { useLayoutEffect, useRef, useEffect } from 'react';
import { useTranslation } from '../common/i18n/useTranslation';
import { useNavigation, useWindowDimensions } from '../utils/customHooks';
import { WbGrid, WbGridCol, WbGridRow, WbLink, WbText } from '@workbench/react';
import { useContentRefContext } from './contexts/ContentRefProvider';
import { makeStyles } from 'tss-react/mui';
import { FooterTabNamesEnum } from '../utils/enums';
import { Routes } from '../routes/routes';
import { useMatch } from 'react-router-dom';
import BuildInfo from '../common/components/BuildInfo';

const useStyles = makeStyles()((theme: Theme) => ({
    footer: {
        position: 'fixed',
        top: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        gap: theme.spacing(2),
        backgroundColor: theme.palette.common.black,
        padding: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
        },
    },
    links: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
    },
    copyrightText: {
        color: theme.palette.grey[400],
        [theme.breakpoints.down('sm')]: {
            textAlign: 'center',
        },
    },
}));

interface FooterProps {
    setInitialFooterModalTab: (tabName: string) => void;
    showFooterModal: () => void;
}

function Footer(props: Readonly<FooterProps>) {
    const footerRef = useRef<HTMLWbGridElement>(null);
    const { classes } = useStyles();
    const translate = useTranslation();
    const { width, height } = useWindowDimensions();
    const { setFooterSize } = useContentRefContext();
    const isAdminPage = useMatch(`/${Routes.admin}/*`);
    const navigateTo = useNavigation();

    useLayoutEffect(() => {
        if (footerRef.current) {
            setFooterSize(footerRef.current.getBoundingClientRect().height);
        }
    }, [width, height, setFooterSize]);

    useEffect(() => {
        if (footerRef.current) {
            setFooterSize(footerRef.current.getBoundingClientRect().height);
        }
    }, [footerRef.current]);

    const showFooterModal = (tabName: string) => {
        props.setInitialFooterModalTab(tabName);
        props.showFooterModal();
    };

    return (
        <footer className={classes.footer} ref={footerRef}>
            <Toolbar>
                <WbGrid>
                    <WbGridRow>
                        <WbGridCol mq1={12} className={classes.links}>
                            <WbText size="m" className={classes.copyrightText}>
                                Copyright © {new Date().getFullYear()}, Mercedes-Benz AG.
                            </WbText>
                            <WbLink
                                variant="small"
                                theme="dark"
                                onClick={() => {
                                    showFooterModal(FooterTabNamesEnum.PROVIDER);
                                }}
                            >
                                {translate('provider')}
                            </WbLink>
                            <WbLink
                                variant="small"
                                theme="dark"
                                onClick={() => {
                                    showFooterModal(FooterTabNamesEnum.LEGAL_NOTICES);
                                }}
                            >
                                {translate('legal_notices')}
                            </WbLink>
                            <WbLink
                                variant="small"
                                theme="dark"
                                onClick={() => {
                                    showFooterModal(FooterTabNamesEnum.SETTINGS);
                                }}
                            >
                                {translate('settings')}
                            </WbLink>
                            <WbLink
                                variant="small"
                                theme="dark"
                                onClick={() => {
                                    showFooterModal(FooterTabNamesEnum.DATA_PROTECTION);
                                }}
                            >
                                {translate('data_protection')}
                            </WbLink>
                            <WbLink
                                variant="small"
                                theme="dark"
                                onClick={() => {
                                    showFooterModal(FooterTabNamesEnum.COPYRIGHT_NOTICES_AND_LICENSES);
                                }}
                            >
                                {translate('copyright_notices_and_licenses')}
                            </WbLink>
                            {!isAdminPage && (
                                <WbLink variant="small" theme="dark" onClick={() => navigateTo(Routes.contact)}>
                                    {translate('contact')}
                                </WbLink>
                            )}
                        </WbGridCol>
                    </WbGridRow>
                </WbGrid>
                <BuildInfo />
            </Toolbar>
        </footer>
    );
}

export default Footer;
